'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, Filter, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FilterSheetField } from '@/components/shared/FilterSheetField'
import {
  FilterSheetSelect,
  FilterSheetSelectContent,
  FilterSheetSelectItem,
  FilterSheetSelectTrigger,
  FilterSheetSelectValue,
} from '@/components/shared/FilterSheetSelect'
import {
  filterDropdownTriggerClassName,
  filterSheetActionButtonClassName,
} from '@/components/shared/filterSheetStyles'
import { CitySelector } from '@/components/shared/CitySelector'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useCities } from '@/hooks/useCities'
import type { BoardUrlFilterState } from '@/hooks/useBoardUrlFilters'
import type { CommunityPostTypeFilter, CommunitySortMode } from '@/lib/communityPostData'
import {
  buildAccordionRegions,
  expandLocationTokensToLaunchedCities,
  getMultiLocationFilterDisplay,
  isEffectivelyAllLocations,
} from '@/lib/locationRegions'
import { cn } from '@/lib/utils'

interface BoardFilterSheetProps {
  onOpenChange: (open: boolean) => void
  appliedFilters: BoardUrlFilterState
  onApply: (draft: BoardUrlFilterState) => void
}

const POST_TYPE_OPTIONS: readonly CommunityPostTypeFilter[] = [
  'all',
  'flash_drop',
  'new_tattoo',
  'deal',
  'general',
]

function buildDraftCitiesFromAppliedFilters(
  appliedFilters: BoardUrlFilterState,
  launchedCities: readonly string[],
  allLocationsText: string,
): string[] {
  if (launchedCities.length === 0) {
    return []
  }

  if (isEffectivelyAllLocations(appliedFilters.selectedLocations, allLocationsText, launchedCities)) {
    return []
  }

  return expandLocationTokensToLaunchedCities(
    appliedFilters.selectedLocations,
    allLocationsText,
    new Set(launchedCities),
  )
}

function isSheetNestedOverlayTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest('[data-radix-select-content]') ||
      target.closest('[data-radix-select-viewport]') ||
      target.closest('[data-radix-popover-content]') ||
      target.closest('[role="listbox"]'),
  )
}

function preventSheetDismissForNestedOverlay(event: Event) {
  if (isSheetNestedOverlayTarget(event.target)) {
    event.preventDefault()
  }
}

export function BoardFilterSheet({
  onOpenChange,
  appliedFilters,
  onApply,
}: BoardFilterSheetProps) {
  const { t } = useTranslation()
  const {
    cities: launchedCities,
    cityArtistCountByCity,
    cityCountryByCity,
    loading: citiesLoading,
  } = useCities()
  const allLocationsText = t('onskin.filters.allLocations')

  const [draft, setDraft] = React.useState<BoardUrlFilterState>(() => appliedFilters)
  const [draftCities, setDraftCities] = React.useState<string[]>(() =>
    buildDraftCitiesFromAppliedFilters(appliedFilters, launchedCities, allLocationsText),
  )
  const locationAccordionRegions = React.useMemo(
    () => buildAccordionRegions(launchedCities, cityCountryByCity ?? {}),
    [launchedCities, cityCountryByCity],
  )

  const launchedCitiesKey = launchedCities.join('\0')
  const previousLaunchedCitiesKeyRef = React.useRef(launchedCitiesKey)

  React.useEffect(() => {
    const previousLaunchedCitiesKey = previousLaunchedCitiesKeyRef.current
    previousLaunchedCitiesKeyRef.current = launchedCitiesKey

    if (previousLaunchedCitiesKey.length > 0 || launchedCitiesKey.length === 0) {
      return
    }

    setDraftCities(
      buildDraftCitiesFromAppliedFilters(appliedFilters, launchedCities, allLocationsText),
    )
  }, [allLocationsText, appliedFilters, launchedCities, launchedCitiesKey])

  const locationDisplayText = React.useMemo(() => {
    if (launchedCities.length === 0 || draftCities.length === 0) {
      return allLocationsText
    }
    return getMultiLocationFilterDisplay(draftCities, allLocationsText, locationAccordionRegions)
  }, [allLocationsText, draftCities, launchedCities.length, locationAccordionRegions])

  const handleApply = () => {
    const nextLocations =
      draftCities.length === 0 || launchedCities.length === 0 ? [] : draftCities

    onApply({
      ...draft,
      selectedLocations: nextLocations,
    })
    onOpenChange(false)
  }

  const handleReset = () => {
    const resetDraft: BoardUrlFilterState = {
      selectedLocations: [],
      sortMode: 'topWeek',
      postTypeFilter: 'all',
    }
    setDraft(resetDraft)
    setDraftCities([])
    onApply(resetDraft)
    onOpenChange(false)
  }

  return (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-auto max-h-[85vh] flex-col p-6"
        hideCloseButton
        onPointerDownOutside={preventSheetDismissForNestedOverlay}
        onInteractOutside={preventSheetDismissForNestedOverlay}
        onFocusOutside={preventSheetDismissForNestedOverlay}
      >
        <SheetHeader className="shrink-0 space-y-0 border-b border-border/40 pb-4">
          <SheetTitle className="flex items-center gap-2 text-xl font-semibold">
            <Filter className="h-5 w-5 shrink-0" aria-hidden />
            {t('board.filters.title', 'Board filters')}
          </SheetTitle>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto py-5">
          <FilterSheetField
            label={t('filters.location', 'Location')}
            icon={<MapPin aria-hidden />}
          >
            {citiesLoading ? (
              <div className={cn(filterDropdownTriggerClassName, 'text-muted-foreground')}>
                {t('common.loading', 'Loading...')}
              </div>
            ) : (
              <CitySelector
                value={locationDisplayText}
                filterCities={launchedCities}
                artistCountByCity={cityArtistCountByCity}
                placeholder={allLocationsText}
                dropdownMode="search"
                layout="popover"
                variant="filterSheet"
                multiSelect
                selectedCities={draftCities}
                onMultiChange={setDraftCities}
                searchPlaceholder={t('filters.searchCities', 'Search cities...')}
              />
            )}
          </FilterSheetField>

          <FilterSheetField
            label={t('board.filters.sort', 'Sort')}
            icon={<ArrowUpDown aria-hidden />}
          >
            <FilterSheetSelect
              value={draft.sortMode}
              onValueChange={(value: CommunitySortMode) =>
                setDraft((current) => ({ ...current, sortMode: value }))
              }
            >
              <FilterSheetSelectTrigger>
                <FilterSheetSelectValue>
                  {draft.sortMode === 'recent'
                    ? t('community.sortRecent')
                    : t('community.sortTopWeek')}
                </FilterSheetSelectValue>
              </FilterSheetSelectTrigger>
              <FilterSheetSelectContent position="popper">
                <FilterSheetSelectItem value="topWeek">{t('community.sortTopWeek')}</FilterSheetSelectItem>
                <FilterSheetSelectItem value="recent">{t('community.sortRecent')}</FilterSheetSelectItem>
              </FilterSheetSelectContent>
            </FilterSheetSelect>
          </FilterSheetField>

          <FilterSheetField
            label={t('board.filters.postType', 'Post type')}
            icon={<Filter aria-hidden />}
          >
            <FilterSheetSelect
              value={draft.postTypeFilter}
              onValueChange={(value: CommunityPostTypeFilter) =>
                setDraft((current) => ({ ...current, postTypeFilter: value }))
              }
            >
              <FilterSheetSelectTrigger>
                <FilterSheetSelectValue>
                  {draft.postTypeFilter === 'all'
                    ? t('board.filters.allPostTypes')
                    : t(`board.postTypes.${draft.postTypeFilter}`)}
                </FilterSheetSelectValue>
              </FilterSheetSelectTrigger>
              <FilterSheetSelectContent position="popper">
                {POST_TYPE_OPTIONS.map((option) => (
                  <FilterSheetSelectItem key={option} value={option}>
                    {option === 'all'
                      ? t('board.filters.allPostTypes')
                      : t(`board.postTypes.${option}`)}
                  </FilterSheetSelectItem>
                ))}
              </FilterSheetSelectContent>
            </FilterSheetSelect>
          </FilterSheetField>
        </div>

        <div className="flex shrink-0 gap-3 border-t border-border/40 pt-5">
          <Button
            type="button"
            variant="secondary"
            className={filterSheetActionButtonClassName}
            onClick={handleReset}
          >
            {t('filters.clear', 'Clear')}
          </Button>
          <Button
            type="button"
            className={filterSheetActionButtonClassName}
            onClick={handleApply}
          >
            {t('common.apply', 'Apply')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
